import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "input", "messages", "sendButton", "closeButton"]
  static values = { companySlug: String }

  connect() {
    console.log("ChatWidgetController connected")
    this.initializeChatWidget()
  }

  disconnect() {
    console.log("ChatWidgetController disconnected")
  }

  initializeChatWidget() {
    // Set up event listeners
    this.setupEventListeners()
  }

  setupEventListeners() {
    // Use event delegation on the controller element (modal) for buttons that might not exist yet
    this.element.addEventListener('click', (e) => {
      // Close button click - check if clicked on button or any child element (like SVG)
      const closeButton = e.target.closest('#close-chat-widget')
      if (closeButton) {
        e.preventDefault()
        e.stopPropagation()
        this.closeChat()
        return
      }
      
      // Send button click
      const sendButton = e.target.closest('#send-message')
      if (sendButton) {
        e.preventDefault()
        this.sendMessage()
        return
      }
      
      // Close on backdrop click (clicking on the modal background itself)
      if (e.target.id === 'chat-widget-modal') {
        this.closeChat()
        return
      }
    })
    
    // Also add direct listener as fallback
    const closeButton = document.getElementById('close-chat-widget')
    if (closeButton) {
      closeButton.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.closeChat()
      })
    }

    // Send message on Enter key
    const chatInput = document.getElementById('chat-input')
    if (chatInput) {
      chatInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          this.sendMessage()
        }
      })
    }
  }

  openChat() {
    const chatModal = document.getElementById('chat-widget-modal')
    const chatInput = document.getElementById('chat-input')
    
    if (chatModal) {
      chatModal.classList.remove('hidden')
      if (chatInput) {
        chatInput.focus()
      }
    }
  }

  // Also expose as a static method for inline onclick handlers
  static openChat() {
    const chatModal = document.getElementById('chat-widget-modal')
    const chatInput = document.getElementById('chat-input')
    
    if (chatModal) {
      chatModal.classList.remove('hidden')
      if (chatInput) {
        chatInput.focus()
      }
    }
  }

  closeChat() {
    const chatModal = document.getElementById('chat-widget-modal')
    if (chatModal) {
      chatModal.classList.add('hidden')
    }
  }

  sendMessage() {
    const chatInput = document.getElementById('chat-input')
    if (!chatInput) return

    const message = chatInput.value.trim()
    if (message) {
      // Add user message immediately
      this.addMessage(message, 'user')
      chatInput.value = ''
      
      // Show typing indicator
      this.showTypingIndicator()
      
      // Send to ChatGPT API
      this.sendToChatGPT(message)
    }
  }

  sendToChatGPT(message) {
    const url = `/api/companies/${this.companySlugValue}/rag/chat`
    
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({ query: message })
    })
    .then(response => response.json())
    .then(data => {
      this.hideTypingIndicator()
      
      // Ensure we have a string response, not an object
      let responseText = null
      
      if (data.response) {
        // If response is an object, try to extract content or stringify it
        if (typeof data.response === 'object') {
          responseText = data.response.content || data.response.text || data.response.message || JSON.stringify(data.response)
        } else {
          responseText = String(data.response)
        }
      } else if (data.error) {
        // Handle error - ensure it's a string
        if (typeof data.error === 'object') {
          responseText = `Error: ${data.error.message || data.error.error || JSON.stringify(data.error)}`
        } else {
          responseText = `Error: ${data.error}`
        }
      }
      
      if (responseText) {
        this.addMessage(responseText, 'ai')
      } else {
        console.error('Unexpected response format:', data)
        this.addMessage('Sorry, I encountered an error. Please try again.', 'ai')
      }
    })
    .catch(error => {
      this.hideTypingIndicator()
      console.error('RAG API Error:', error)
      this.addMessage('Sorry, I encountered an error. Please try again.', 'ai')
    })
  }

  showTypingIndicator() {
    const chatMessages = document.getElementById('chat-messages')
    if (!chatMessages) return

    const typingDiv = document.createElement('div')
    typingDiv.id = 'typing-indicator'
    typingDiv.className = 'flex items-start space-x-3'
    typingDiv.innerHTML = `
      <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
        <svg class="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      </div>
      <div class="bg-white rounded-lg p-3 shadow-sm max-w-xs">
        <div class="flex space-x-1">
          <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
          <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.1s"></div>
          <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
        </div>
      </div>
    `
    
    const messagesContainer = chatMessages.querySelector('.space-y-4')
    if (messagesContainer) {
      messagesContainer.appendChild(typingDiv)
      chatMessages.scrollTop = chatMessages.scrollHeight
    }
  }

  hideTypingIndicator() {
    const typingIndicator = document.getElementById('typing-indicator')
    if (typingIndicator) {
      typingIndicator.remove()
    }
  }

  addMessage(text, sender, scroll = true) {
    const chatMessages = document.getElementById('chat-messages')
    if (!chatMessages) return

    const messageDiv = document.createElement('div')
    messageDiv.className = 'flex items-start space-x-3'
    
    if (sender === 'user') {
      messageDiv.className += ' flex-row-reverse space-x-reverse'
      messageDiv.innerHTML = `
        <div class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
          <svg class="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"></path>
          </svg>
        </div>
        <div class="bg-green-600 text-white rounded-lg p-3 shadow-sm max-w-xs">
          <p class="text-sm">${text}</p>
          <p class="text-xs text-green-100 mt-1">Just now</p>
        </div>
      `
    } else {
      messageDiv.innerHTML = `
        <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
          <svg class="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <div class="bg-white rounded-lg p-3 shadow-sm max-w-xs">
          <p class="text-sm text-gray-800">${text}</p>
          <p class="text-xs text-gray-500 mt-1">Just now</p>
        </div>
      `
    }
    
    const messagesContainer = chatMessages.querySelector('.space-y-4')
    if (messagesContainer) {
      messagesContainer.appendChild(messageDiv)
      if (scroll) {
        chatMessages.scrollTop = chatMessages.scrollHeight
      }
    }
  }
}
